import { Stack, Tab, Tabs } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { NavLink, useLocation } from 'react-router';
import { ROUTES, getRouteTitle } from '../../../../constants/RouteNameMapping';
import { KPIRequestStatus } from '../../../../data-models/kpi-requests.data-model';
import {
  ClientSettingsPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../../../data-models/preferences-v3.data-model';
import { PersonaKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import { kpiRequestsCountStateJ } from '../../../../services/state/KPI/KPIRequestsStateJ';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { PreferencesServiceV3 } from '../../../../util/preferences-service-v3';
import { CountBadge, KPITabSx } from './TabComponents';
import { KpiRequestStatusByGroup } from './KPIResponsesGrid';
import { useAtomValue } from 'jotai';

export default function KPIResponsesTabs() {
  const { pathname } = useLocation();
  const requestCount = useAtomValue(kpiRequestsCountStateJ);
  const tabs = useResponseTabs(pathname, requestCount);

  return (
    <Tabs variant='scrollable' scrollButtons='auto' value={pathname}>
      {tabs.map((t) => (
        <Tab
          component={NavLink}
          key={t.path}
          value={t.path}
          label={t.title}
          style={{ paddingLeft: 0, marginRight: '1rem' }}
          to={{ pathname: `${t.path}` }}
          sx={KPITabSx}
        />
      ))}
    </Tabs>
  );
}

function useResponseTabs(pathname: string, requestCount: Record<KPIRequestStatus, number>) {
  const { useManagedServices } = useFlags<MaggieFeatureFlags>();
  const showMs =
    useManagedServices &&
    Boolean(
      PreferencesServiceV3.get().getPreference<ClientSettingsPreferences>(
        PreferenceKey.clientSettings,
        PreferenceScope.ORGANIZATION
      )?.managedServices
    );
  if (PermissionService.get().hasPersona(PersonaKey.managedServices)) {
    return getTabsForMsRole(pathname, requestCount);
  }
  return getDefaultTabs(pathname, requestCount, showMs);
}

function getDefaultTabs(
  pathname: string,
  requestCount: Record<KPIRequestStatus, number>,
  showManagedServices: boolean
) {
  const toReviewWithMs = KpiRequestStatusByGroup[KPIRequestStatus.Submitted];
  const submittedCount = !showManagedServices
    ? requestCount[KPIRequestStatus.Submitted]
    : toReviewWithMs.reduce((acc, curr) => {
        return acc + requestCount[curr];
      }, 0);
  const result = [
    {
      path: `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}`,
      title: (
        <Stack direction='row'>
          To Review
          <CountBadge
            badgeContent={submittedCount}
            active={pathname === `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}`}
          />
        </Stack>
      ),
    },
    {
      path: `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}/${ROUTES.KPI_RESPONSES_ACCEPTED}`,
      title: getRouteTitle(ROUTES.KPI_RESPONSES_ACCEPTED),
    },
    {
      path: `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}/${ROUTES.KPI_RESPONSES_REJECTED}`,
      title: getRouteTitle(ROUTES.KPI_RESPONSES_REJECTED),
    },
  ];
  if (showManagedServices) {
    result.splice(1, 0, {
      path: `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}/${ROUTES.KPI_RESPONSES_MS_REVIEWED}`,
      title: getRouteTitle(ROUTES.KPI_RESPONSES_MS_REVIEWED),
    });
  }
  return result;
}

function getTabsForMsRole(pathname: string, requestCount: Record<KPIRequestStatus, number>) {
  return [
    {
      path: `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}/${ROUTES.KPI_RESPONSES_MS_PENDING}`,
      title: (
        <Stack direction='row'>
          {getRouteTitle(ROUTES.KPI_RESPONSES_MS_PENDING)}
          <CountBadge
            badgeContent={requestCount[KPIRequestStatus.ManagedServiceSubmitted]}
            active={pathname === `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}/${ROUTES.KPI_RESPONSES_MS_PENDING}`}
          />
        </Stack>
      ),
    },
    {
      path: `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}/${ROUTES.KPI_RESPONSES_MS_IN_PROGRESS}`,
      title: getRouteTitle(ROUTES.KPI_RESPONSES_MS_IN_PROGRESS),
    },
    {
      path: `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}/${ROUTES.KPI_RESPONSES_MS_REVIEWED}`,
      title: getRouteTitle(ROUTES.KPI_RESPONSES_MS_REVIEWED),
    },
    {
      path: `/${ROUTES.KPI}/${ROUTES.KPI_RESPONSES}/${ROUTES.KPI_RESPONSES_REJECTED}`,
      title: getRouteTitle(ROUTES.KPI_RESPONSES_REJECTED),
    },
  ];
}
